import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classnames from 'classnames'
import { Field, useFormikContext } from 'formik'
import * as React from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import Swal from 'sweetalert2'
import type { AdministratorStudentChoicesOutletContext } from './AdministratorStudentChoicesLayout'
import { ChoiceCreditCells } from './ChoiceCreditCells'
import {
  CHOICE_INPUT_CLASS_NAME,
  choicePatchBodyOf,
  choiceRowValuesOf,
  editableFieldsOf,
  hasRowChanged,
  useStudentChoiceParams,
  type ChoicesFormValues,
} from './constants'
import { useChoiceScoreFormatter, type ScoreFormatter } from './useChoiceScoreFormatter'

const EMPTY_VALUE = '-'

export default function AdministratorStudentChoicesCompletedCoursesPage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('choice:completed_courses')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const { uid, profileId } = useStudentChoiceParams()
  const { targetSemesterId } = useOutletContext<AdministratorStudentChoicesOutletContext>()

  const [editingUids, setEditingUids] = React.useState<ReadonlySet<string>>(() => new Set())

  const {
    data: choicesPage,
    mutate,
    isLoading,
  } = useApi({
    endpoint: Api.getAdministratorStudentChoices,
    params: React.useMemo(
      () => ({
        headers,
        args: { uid, profileId },
        query: targetSemesterId != null ? { targetSemesterId } : {},
      }),
      [headers, uid, profileId, targetSemesterId]
    ),
    shouldFetch: uid !== '' && profileId !== '' && targetSemesterId != null,
    suspense: false,
  })

  const formatScore = useChoiceScoreFormatter()

  const passedEntries = React.useMemo(
    () =>
      (choicesPage?.passedChoicesBySemester ?? []).flatMap((group) =>
        group.choices.map((choice) => ({ choice, semesterName: group.semester.name }))
      ),
    [choicesPage?.passedChoicesBySemester]
  )

  const editableChoices = React.useMemo(
    () =>
      passedEntries
        .map((entry) => entry.choice)
        .filter((choice) => choice.capabilities?.update === true && editableFieldsOf(choice).length > 0),
    [passedEntries]
  )

  const toggleEditing = React.useCallback((choiceUid: string) => {
    setEditingUids((current) => {
      const next = new Set(current)
      if (!next.delete(choiceUid)) next.add(choiceUid)
      return next
    })
  }, [])

  const toggleEditingAll = React.useCallback(() => {
    setEditingUids((current) =>
      current.size === editableChoices.length ? new Set() : new Set(editableChoices.map((choice) => choice.uid))
    )
  }, [editableChoices])

  const stopEditing = React.useCallback(() => setEditingUids(new Set()), [])

  const editInitialValues = React.useMemo<ChoicesFormValues>(
    () => ({
      rows: Object.fromEntries(passedEntries.map(({ choice }) => [choice.uid, choiceRowValuesOf(choice)])),
    }),
    [passedEntries]
  )

  const handleUpdate = React.useCallback(
    async (values: ChoicesFormValues) => {
      // There is no bulk endpoint, so the open rows are patched one by one, each against its own semester.
      const failures: { readonly choice: Api.AdministratorChoiceRow; readonly message: string }[] = []
      let updatedCount = 0

      for (const { choice } of passedEntries) {
        if (!editingUids.has(choice.uid)) continue
        const row = values.rows[choice.uid]
        if (row == null) continue
        const allowed = editableFieldsOf(choice)
        if (!hasRowChanged(choice, row, allowed)) continue
        try {
          await Api.patchAdministratorStudentChoice({
            headers,
            args: { uid, profileId, choiceUid: choice.uid },
            body: {
              targetSemesterId: choice.semId,
              ...choicePatchBodyOf(choice, row, allowed),
            },
          })
          updatedCount++
        } catch (error) {
          failures.push({ choice, message: errorMessage(error as base.CommonError) })
        }
      }

      // Rejected rows stay open so they can be corrected; the saved ones close.
      setEditingUids(new Set(failures.map((failure) => failure.choice.uid)))
      await mutate()

      if (failures.length > 0) {
        toast.error(failures.map((failure) => `${failure.choice.courseName}: ${failure.message}`).join('\n'))
        return
      }
      if (updatedCount > 0) toast.success(t('common:updated_successfully'))
    },
    [editingUids, headers, mutate, passedEntries, profileId, t, uid]
  )

  const handleToggleDiscard = React.useCallback(
    async (choice: Api.AdministratorChoiceRow) => {
      const params = {
        headers,
        args: { uid, profileId, choiceUid: choice.uid },
        query: { targetSemesterId: choice.semId },
      }
      try {
        if (choice.isDiscarded === true) {
          await Api.patchAdministratorStudentChoiceUndiscard(params)
        } else {
          await Api.patchAdministratorStudentChoiceDiscard(params)
        }
        await mutate()
        toast.success(t('common:updated_successfully'))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      }
    },
    [headers, mutate, profileId, t, uid]
  )

  const handleDelete = React.useCallback(
    async (choice: Api.AdministratorChoiceRow) => {
      const alert = await Swal.fire({
        title: t('common:are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6C757D',
      })
      if (!alert.isConfirmed) return
      try {
        await Api.deleteAdministratorStudentChoice({
          headers,
          args: { uid, profileId, choiceUid: choice.uid },
          query: { targetSemesterId: choice.semId },
        })
        await mutate()
        toast.success(t('common:successfully_deleted'))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      }
    },
    [headers, mutate, profileId, t, uid]
  )

  const currentSemester = choicesPage?.targetSemester ?? choicesPage?.activeSemester

  const currentEntries = React.useMemo(
    () =>
      (choicesPage?.targetSemesterChoices ?? choicesPage?.currentChoices ?? []).map((choice) => ({
        choice,
        semesterName: currentSemester?.name ?? EMPTY_VALUE,
      })),
    [choicesPage?.currentChoices, choicesPage?.targetSemesterChoices, currentSemester?.name]
  )

  if (isLoading) return <Loader className="m-auto mt-8 flex" />

  if (choicesPage == null) return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />

  const studentFaculty = choicesPage.student.faculty

  return (
    <div className="mt-4 w-full space-y-8">
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-primaryTextColor">{t('choice:previous_semesters')}</h2>
        <Form initialValues={editInitialValues} enableReinitialize onSubmit={handleUpdate}>
          <PassedChoicesEditor
            entries={passedEntries}
            studentFaculty={studentFaculty}
            editableChoices={editableChoices}
            editingUids={editingUids}
            formatScore={formatScore}
            onToggleEditing={toggleEditing}
            onToggleEditingAll={toggleEditingAll}
            onStopEditing={stopEditing}
            onToggleDiscard={handleToggleDiscard}
            onDelete={handleDelete}
          />
        </Form>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-primaryTextColor">
          {t('choice:current_semester')}
          {currentSemester != null && <span className="ml-2 text-sm text-captionColor">{currentSemester.name}</span>}
        </h2>
        <ChoicesTable entries={currentEntries} studentFaculty={studentFaculty} formatScore={formatScore} />
      </section>
    </div>
  )
}

interface ChoiceEntry {
  readonly choice: Api.AdministratorChoiceRow
  readonly semesterName: string
}

interface ChoiceGroup {
  readonly key: string
  readonly title: string
  readonly entries: readonly ChoiceEntry[]
}

const GENERAL_MODULE_GROUP_KEY = 'general'

function courseNameOf(choice: Api.AdministratorChoiceRow, locale: string): string {
  return locale === 'en' ? (choice.courseNameEn ?? choice.courseName) : choice.courseName
}

/**
 * Legacy splits a table into the general module first (always shown, even when empty), then one
 * group per faculty that owns the course, with each group's courses in alphabetical order. Rows
 * without their own `faculty` fall back to the student's major faculty.
 */
function groupChoices(
  entries: readonly ChoiceEntry[],
  studentFaculty: Api.AdministratorChoiceStudentFaculty | null,
  generalModuleTitle: string,
  locale: string
): readonly ChoiceGroup[] {
  // Stored names carry stray spaces ("სემინარი  II"), which would otherwise sort "II" before "I".
  const sortKeyOf = (entry: ChoiceEntry): string => courseNameOf(entry.choice, locale).replace(/\s+/g, ' ').trim()
  const sortedEntries = [...entries].sort((a, b) => sortKeyOf(a).localeCompare(sortKeyOf(b), locale, { numeric: true }))

  const general: ChoiceEntry[] = []
  const byFaculty = new Map<string, { title: string; entries: ChoiceEntry[] }>()

  for (const entry of sortedEntries) {
    if (entry.choice.couGeneral === 1) {
      general.push(entry)
      continue
    }
    const faculty = entry.choice.faculty ?? studentFaculty
    const key = `faculty-${faculty?.id ?? 'unknown'}`
    const group = byFaculty.get(key) ?? { title: faculty?.name ?? EMPTY_VALUE, entries: [] }
    group.entries.push(entry)
    byFaculty.set(key, group)
  }

  return [
    { key: GENERAL_MODULE_GROUP_KEY, title: generalModuleTitle, entries: general },
    ...Array.from(byFaculty, ([key, group]) => ({ key, ...group })),
  ]
}

function sumCredits(choices: readonly Api.AdministratorChoiceRow[]): {
  readonly courseCredits: number
  readonly credits: number
} {
  const round = (value: number): number => Math.round(value * 100) / 100
  return {
    courseCredits: round(choices.reduce((sum, choice) => sum + choice.courseCredits, 0)),
    credits: round(choices.reduce((sum, choice) => sum + choice.credits, 0)),
  }
}

interface RowActions {
  readonly onToggleEditing?: (choiceUid: string) => void
  readonly onToggleDiscard?: (choice: Api.AdministratorChoiceRow) => Promise<void>
  readonly onDelete?: (choice: Api.AdministratorChoiceRow) => Promise<void>
}

interface PassedChoicesEditorProps extends RowActions {
  readonly entries: readonly ChoiceEntry[]
  readonly studentFaculty: Api.AdministratorChoiceStudentFaculty | null
  readonly editableChoices: readonly Api.AdministratorChoiceRow[]
  readonly editingUids: ReadonlySet<string>
  readonly formatScore: ScoreFormatter
  readonly onToggleEditing: (choiceUid: string) => void
  readonly onToggleEditingAll: () => void
  readonly onStopEditing: () => void
}

function PassedChoicesEditor({
  editableChoices,
  editingUids,
  onToggleEditing,
  onToggleEditingAll,
  onStopEditing,
  ...tableProps
}: PassedChoicesEditorProps): JSX.Element {
  const t = useTranslatable()
  const formik = useFormikContext<ChoicesFormValues>()

  const isEditingAll = editableChoices.length > 0 && editingUids.size === editableChoices.length

  const handleToggleEditing = React.useCallback(
    (choiceUid: string) => {
      // Closing a row drops what was typed into it.
      if (editingUids.has(choiceUid)) {
        const initialRow = formik.initialValues.rows[choiceUid]
        if (initialRow != null) void formik.setFieldValue(`rows.${choiceUid}`, initialRow)
      }
      onToggleEditing(choiceUid)
    },
    [editingUids, formik, onToggleEditing]
  )

  const handleCancel = React.useCallback(() => {
    formik.resetForm()
    onStopEditing()
  }, [formik, onStopEditing])

  return (
    <ChoicesTable
      {...tableProps}
      editingUids={editingUids}
      onToggleEditing={handleToggleEditing}
      headerAction={
        editableChoices.length > 0 ? (
          <button
            type="button"
            className="whitespace-nowrap text-caption text-primaryBlueLink hover:underline"
            onClick={onToggleEditingAll}
          >
            {isEditingAll ? t('choice:stop_editing_all') : t('choice:edit_all')}
          </button>
        ) : undefined
      }
    >
      {editingUids.size > 0 && (
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={handleCancel}>
            {t('common:cancel')}
          </Button>
          <Button variant="blue" type="submit">
            {t('common:update')}
          </Button>
        </div>
      )}
    </ChoicesTable>
  )
}

interface ChoicesTableProps extends RowActions {
  readonly entries: readonly ChoiceEntry[]
  readonly studentFaculty: Api.AdministratorChoiceStudentFaculty | null
  readonly formatScore: ScoreFormatter
  readonly editingUids?: ReadonlySet<string>
  readonly headerAction?: React.ReactNode
  readonly children?: React.ReactNode
}

function ChoicesTable({
  entries,
  studentFaculty,
  headerAction,
  children,
  ...groupProps
}: ChoicesTableProps): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const generalModuleTitle = t('course:general_module')

  const groups = React.useMemo(
    () => groupChoices(entries, studentFaculty, generalModuleTitle, locale),
    [entries, generalModuleTitle, locale, studentFaculty]
  )
  const totals = sumCredits(entries.map((entry) => entry.choice))

  return (
    <fieldset className="min-w-0 rounded-card bg-card px-6 py-8">
      <Table.Table className="w-full table-auto">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('course:course_name')}</Table.Th>
            <Table.Th>{t('common:code')}</Table.Th>
            <Table.Th>{t('course:course_credits')}</Table.Th>
            <Table.Th>{t('common:gain_credits')}</Table.Th>
            <Table.Th>{t('common:score')}</Table.Th>
            <Table.Th>{t('group:group')}</Table.Th>
            <Table.Th>{t('course:teachers')}</Table.Th>
            <Table.Th>{t('common:semester')}</Table.Th>
            <Table.Th>{headerAction}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        {groups.map((group) => (
          <ChoiceGroupRows key={group.key} group={group} {...groupProps} />
        ))}
        <Table.Tfoot>
          <Table.Tr className="font-semibold">
            <Table.Td />
            <Table.Td className="text-end">{t('common:total')}</Table.Td>
            <Table.Td>{totals.courseCredits}</Table.Td>
            <Table.Td>{totals.credits}</Table.Td>
            <Table.Td colSpan={5} />
          </Table.Tr>
        </Table.Tfoot>
      </Table.Table>
      {children}
    </fieldset>
  )
}

interface ChoiceGroupRowsProps extends RowActions {
  readonly group: ChoiceGroup
  readonly formatScore: ScoreFormatter
  readonly editingUids?: ReadonlySet<string>
}

function ChoiceGroupRows({ group, editingUids, ...rowProps }: ChoiceGroupRowsProps): JSX.Element {
  const t = useTranslatable()
  const subtotals = sumCredits(group.entries.map((entry) => entry.choice))

  return (
    <Table.Tbody>
      <Table.Tr>
        <Table.Td colSpan={9} className="rounded-lg font-semibold">
          {group.title}
        </Table.Td>
      </Table.Tr>
      {group.entries.length === 0 ? (
        <Table.Tr>
          <Table.Td colSpan={9} className="text-center text-captionColor">
            {t('error:courses_not_found')}
          </Table.Td>
        </Table.Tr>
      ) : (
        <>
          {group.entries.map(({ choice, semesterName }) => (
            <ChoiceRow
              key={choice.uid}
              choice={choice}
              semesterName={semesterName}
              isEditing={editingUids?.has(choice.uid) === true}
              {...rowProps}
            />
          ))}
          <Table.Tr className="font-semibold">
            <Table.Td />
            <Table.Td />
            <Table.Td>{subtotals.courseCredits}</Table.Td>
            <Table.Td>{subtotals.credits}</Table.Td>
            <Table.Td colSpan={5} />
          </Table.Tr>
        </>
      )}
    </Table.Tbody>
  )
}

interface ChoiceRowProps extends RowActions {
  readonly choice: Api.AdministratorChoiceRow
  readonly semesterName: string
  readonly isEditing: boolean
  readonly formatScore: ScoreFormatter
}

function ChoiceRow({
  choice,
  semesterName,
  isEditing,
  formatScore,
  onToggleEditing,
  onToggleDiscard,
  onDelete,
}: ChoiceRowProps): JSX.Element {
  const t = useTranslatable()
  const canManageUsers = useAdminAccess().canManage('userStudents')
  const locale = useLocale()

  const editableFields = React.useMemo(() => editableFieldsOf(choice), [choice])
  const canEdit =
    canManageUsers && choice.capabilities?.update === true && editableFields.length > 0 && onToggleEditing != null

  const handleToggleEditing = React.useCallback(() => onToggleEditing?.(choice.uid), [choice.uid, onToggleEditing])
  const handleToggleDiscard = React.useCallback(() => void onToggleDiscard?.(choice), [choice, onToggleDiscard])
  const handleDelete = React.useCallback(() => void onDelete?.(choice), [choice, onDelete])

  const courseName = courseNameOf(choice, locale)
  const teachers =
    choice.teachers != null && choice.teachers.length > 0
      ? choice.teachers.map((teacher) => teacher.fullName).join(', ')
      : EMPTY_VALUE

  return (
    <Table.Tr
      className={classnames({
        'bg-lightRed': choice.rowClass === 'error',
        'bg-lightGreen': choice.rowClass === 'success',
        'bg-lightBlue': choice.rowClass === 'info',
        'opacity-60': choice.isDiscarded === true,
      })}
    >
      <Table.Td className="rounded-l-lg">
        {isEditing && editableFields.includes('couName') ? (
          <Field type="text" name={`rows.${choice.uid}.couName`} className={CHOICE_INPUT_CLASS_NAME} />
        ) : (
          courseName
        )}
        {choice.isDiscarded === true && (
          <span className="ml-2 rounded-md bg-lightRed px-2 py-0.5 text-xs text-primaryTextColor">
            {t('choice:discarded')}
          </span>
        )}
        {isEditing && editableFields.includes('couGeneral') && (
          <label className="mt-2 flex items-center gap-2 text-sm text-primaryTextColor">
            <Field
              type="checkbox"
              name={`rows.${choice.uid}.couGeneral`}
              className="size-4 rounded border-borderGrey"
            />
            {t('course:general_module')}
          </label>
        )}
      </Table.Td>
      <Table.Td className="break-all">
        {isEditing && editableFields.includes('couCode') ? (
          <Field type="text" name={`rows.${choice.uid}.couCode`} className={CHOICE_INPUT_CLASS_NAME} />
        ) : (
          choice.courseCode
        )}
      </Table.Td>
      <ChoiceCreditCells
        choice={choice}
        isEditing={isEditing}
        editableFields={editableFields}
        formattedScore={formatScore(choice)}
      />
      <Table.Td>{choice.group?.name ?? EMPTY_VALUE}</Table.Td>
      <Table.Td>{teachers}</Table.Td>
      <Table.Td>{semesterName}</Table.Td>
      <Table.Td className="rounded-r-lg">
        <div className="flex flex-nowrap items-center">
          {canEdit && (
            <button
              type="button"
              className={classnames('flex size-7 items-center justify-center rounded-full p-1', {
                'bg-table-icon-hover': isEditing,
                'hover:bg-table-icon-hover': !isEditing,
              })}
              onClick={handleToggleEditing}
              aria-label={t('common:edit')}
              aria-pressed={isEditing}
            >
              <FontAwesomeIcon
                icon={ProRegularSvgIcons.faPencil}
                color={isEditing ? undefined : 'gray'}
                className={classnames({ 'text-primaryBlueLink': isEditing })}
                size="lg"
              />
            </button>
          )}
          {canManageUsers &&
            (choice.isDiscarded === true ? choice.capabilities?.undiscard : choice.capabilities?.discard) === true &&
            onToggleDiscard != null && (
              <button
                type="button"
                className="flex size-7 items-center justify-center rounded-full p-1 hover:bg-table-icon-hover"
                onClick={handleToggleDiscard}
                aria-label={choice.isDiscarded === true ? t('choice:undiscard') : t('choice:discard')}
              >
                <FontAwesomeIcon
                  icon={choice.isDiscarded === true ? ProRegularSvgIcons.faRotateLeft : ProRegularSvgIcons.faBan}
                  color="gray"
                  size="lg"
                />
              </button>
            )}
          {canManageUsers && choice.capabilities?.delete === true && onDelete != null && (
            <button
              type="button"
              className="flex size-7 items-center justify-center rounded-full p-1 hover:bg-table-icon-hover"
              onClick={handleDelete}
              aria-label={t('common:delete')}
            >
              <FontAwesomeIcon icon={ProRegularSvgIcons.faTrashCan} color="gray" size="lg" />
            </button>
          )}
          {choice.capabilities?.viewGroup === true && choice.courseId != null && (
            <Link
              to={`/${locale}/administrator/administration/courses/${choice.courseId}/students?semId=${choice.semId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex size-7 items-center justify-center rounded-full p-1 hover:bg-table-icon-hover"
              aria-label={t('course:go_to_course')}
            >
              <FontAwesomeIcon icon={ProRegularSvgIcons.faArrowUpRightFromSquare} color="gray" size="lg" />
            </Link>
          )}
        </div>
      </Table.Td>
    </Table.Tr>
  )
}
